import { USER_CREATED_PAYMENT_CLASS } from '@src/types/constants'
import { CustomerPaymentOperation, OperationInformation } from '@src/types/loan/loan-operation'
import { useCallback, useMemo, useState } from 'react'
import { useLoanSummaryInformation } from '../LoanCalendar-hooks'
import { useLoanCalendarAction } from '../LoanCalendarActionProvider'
import { useLoanCalendarSimulation } from '../LoanCalendarSimulationProvider'
import { useLoanCalendarTableColumns, useTableColumnVisibility } from './LoanCalendarTable-hook'
import LoanCalendarTableCustom from './LoanCalendarTableCustom'
import LoanCalendarTableToolbar from './LoanCalendarTableToolbar'

const LoanCalendarTable = () => {
  const { currentSimulation } = useLoanCalendarSimulation()
  const { selectedPaymentIds, setSelectedPaymentIds: setSelectedPayments } = useLoanCalendarAction()
  const lastPaymentOn = currentSimulation.simulation.fullLoan.loan.lastPaymentOn
  const payments = currentSimulation.simulation.customerPaymentOperations
  const [showMore, setShowMore] = useState(false)
  const columns = useLoanCalendarTableColumns(showMore)
  const columnVisibility = useTableColumnVisibility(columns, showMore)
  const outstandingBalance = useLoanSummaryInformation(currentSimulation.simulation)?.outstandingBalance || 0
  const allLoanRepaymentConfigDates = useMemo(
    () => currentSimulation.simulation.getAllLoanRepaymentConfigDates(),
    [currentSimulation.simulation],
  )

  const getFrequencyConfigIndex = useCallback(
    (date: Date) => {
      for (let i = allLoanRepaymentConfigDates.length - 1; i >= 0; i--) {
        const configDate = allLoanRepaymentConfigDates[i]
        if (date.getTime() >= configDate.getTime()) {
          return i
        }
      }
      return 0
    },
    [allLoanRepaymentConfigDates],
  )

  const getRowClassName = useCallback(
    (row: OperationInformation<CustomerPaymentOperation>, index: number) => {
      const classes: string[] = []

      if (!row.createdBySystem()) {
        classes.push(USER_CREATED_PAYMENT_CLASS)
      }

      // Check if this row is the first row after the last payment date
      const isAfterLastPayment = row.operation.effectiveDate > lastPaymentOn
      if (isAfterLastPayment && index > 0) {
        const previousIsAfterLastPayment = payments[index - 1].operation.effectiveDate > lastPaymentOn
        if (!previousIsAfterLastPayment) {
          // This is the first row after the last payment date
          classes.push(outstandingBalance > 0 ? 'row-after-last-payment-warning' : 'row-after-last-payment-info')
        }
      }

      // Check if this row has a different frequency config than the previous row
      if (index > 0 && allLoanRepaymentConfigDates.length > 1) {
        const currentConfigIndex = getFrequencyConfigIndex(row.operation.effectiveDate)
        const previousConfigIndex = getFrequencyConfigIndex(payments[index - 1].operation.effectiveDate)
        if (currentConfigIndex !== previousConfigIndex) {
          classes.push(`row-frequency-change-${currentConfigIndex}`)
        }
      }

      return classes.join(' ')
    },
    [lastPaymentOn, allLoanRepaymentConfigDates, outstandingBalance, payments, getFrequencyConfigIndex],
  )

  const handleSelectionChange = useCallback(
    (newSelection: Set<string>) => {
      setSelectedPayments(newSelection)
    },
    [setSelectedPayments],
  )

  return (
    <>
      <LoanCalendarTableToolbar />
      <LoanCalendarTableCustom
        rows={payments}
        columns={columns}
        visibleColumns={columnVisibility}
        selectedPaymentIds={selectedPaymentIds}
        onSelectionChange={handleSelectionChange}
        showMore={showMore}
        onShowMoreChange={setShowMore}
        getRowClassName={getRowClassName}
      />
    </>
  )
}

export default LoanCalendarTable
